import type { ScreenerTickerData, ScreenerExchangeData, ScreenerOIDataPoint, ScreenerVolumeDataPoint } from '@/types'

type OIRevision = {
  timestamp?: number | null
  sampleKind?: string | null
  quality?: string | null
  observedAt?: number | null
  readRevision?: number | null
}

export function finiteRevisionValue(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

export function monotonicOISourceAge(
  current: number | null | undefined,
  incoming: number | null | undefined,
): number | null | undefined {
  const currentIsFinite = typeof current === 'number' && Number.isFinite(current)
  const incomingIsFinite = typeof incoming === 'number' && Number.isFinite(incoming)
  if (currentIsFinite && incomingIsFinite) return Math.max(current, incoming)
  if (incomingIsFinite) return incoming
  return current
}

function oiQualityRank(quality: string | null | undefined): number {
  switch (String(quality || '').trim().toLowerCase()) {
    case 'fresh':
      return 3
    case 'observed':
    case 'observed_snapshot':
      return 2
    case 'carried':
    case 'carried_forward':
      return 1
    default:
      return 0
  }
}

function oiSampleKindRank(sampleKind: string | null | undefined): number {
  switch (String(sampleKind || '').trim().toLowerCase()) {
    case 'continuous':
      return 3
    case 'live':
      return 2
    case 'background':
      return 1
    default:
      return 0
  }
}

export function compareOIRevision(left: OIRevision, right: OIRevision): number {
  const timestampDelta = finiteRevisionValue(left.timestamp) - finiteRevisionValue(right.timestamp)
  if (timestampDelta !== 0) return timestampDelta
  // Aggregate provenance describes contributors, not competing raw samples.
  // A correction can legitimately remove a previously continuous contributor.
  if (typeof left.readRevision === 'number' && Number.isFinite(left.readRevision)
    && typeof right.readRevision === 'number' && Number.isFinite(right.readRevision)) {
    const readDelta = left.readRevision - right.readRevision
    if (readDelta !== 0) return readDelta
  }
  const sampleKindDelta = oiSampleKindRank(left.sampleKind) - oiSampleKindRank(right.sampleKind)
  if (sampleKindDelta !== 0) return sampleKindDelta
  // Continuous samples describe actual observations. A later receipt may
  // reveal a stale source, so its lower quality must not preserve an old value.
  const observedDelta = finiteRevisionValue(left.observedAt) - finiteRevisionValue(right.observedAt)
  if (left.sampleKind === 'continuous' && observedDelta !== 0) return observedDelta
  const qualityDelta = oiQualityRank(left.quality) - oiQualityRank(right.quality)
  if (qualityDelta !== 0) return qualityDelta
  return observedDelta || finiteRevisionValue(left.readRevision) - finiteRevisionValue(right.readRevision)
}

export function exchangeOIRevision(data: Partial<ScreenerExchangeData>): OIRevision {
  const history = data.oi_history || []
  const latest = history.length > 0 ? history[history.length - 1] : undefined
  return {
    timestamp: data.oi_updated_at ?? latest?.timestamp,
    sampleKind: data.oi_sample_kind ?? latest?.sampleKind,
    quality: data.oi_quality ?? latest?.quality,
    observedAt: data.oi_observed_at ?? latest?.observedAt,
    readRevision: data.oi_read_revision ?? latest?.readRevision,
  }
}

export function hasOIRevision(data: Partial<ScreenerExchangeData>): boolean {
  return data.oi_updated_at != null
    || data.oi_observed_at != null
    || (data.oi_history?.length || 0) > 0
}

/** Funding can change without a new OI sample; compare the ticker snapshots independently. */
export function mergeFundingSnapshot(
  existing: Partial<ScreenerExchangeData>,
  incoming: Partial<ScreenerExchangeData>,
): Pick<ScreenerExchangeData, 'funding_rate' | 'funding_time' | 'funding_updated_at'> {
  const source = incoming.funding_rate === undefined
    || (existing.funding_rate !== undefined
      && finiteRevisionValue(existing.funding_updated_at) > finiteRevisionValue(incoming.funding_updated_at))
    ? existing : incoming
  return {
    funding_rate: source.funding_rate,
    funding_time: source.funding_time,
    funding_updated_at: source.funding_updated_at,
  }
}

/** Trade segments can arrive after OI; merge their bucket revisions independently. */
export function mergeVolumeHistory(
  existing: Partial<ScreenerExchangeData>,
  incoming: Partial<ScreenerExchangeData>,
  oiHistory: ScreenerOIDataPoint[],
  maxPoints = 500,
): Pick<ScreenerExchangeData, 'volume_history' | 'volume_interval' | 'volume_updated_at'> {
  if (incoming.volume_history === undefined) return {
    volume_history: existing.volume_history,
    volume_interval: existing.volume_interval,
    volume_updated_at: existing.volume_updated_at,
  }
  const histories = existing.volume_interval === incoming.volume_interval ? [existing, incoming] : [incoming]
  const byTimestamp = new Map<number, ScreenerVolumeDataPoint>()
  for (const snapshot of histories) {
    for (const point of snapshot.volume_history || []) {
      if (!Number.isFinite(point.timestamp)) continue
      const previous = byTimestamp.get(point.timestamp)
      const updatedAt = point.updatedAt ?? snapshot.volume_updated_at ?? 0
      if (!previous || finiteRevisionValue(updatedAt) >= finiteRevisionValue(previous.updatedAt)) {
        // Explicit unavailable buckets also replace earlier values; never forward-fill traded volume.
        byTimestamp.set(point.timestamp, { ...point, updatedAt })
      }
    }
  }
  const sorted = [...byTimestamp.values()].sort((a, b) => a.timestamp - b.timestamp)
  const visibleOI = new Set(oiHistory.map((point) => point.timestamp))
  return {
    // A sparse OI source can retain older visible points while volume keeps
    // updating. Keep their exact buckets as well as the latest 500 (at most 1,000).
    volume_history: sorted.filter((point, index) => index >= sorted.length - maxPoints || visibleOI.has(point.timestamp)),
    volume_interval: incoming.volume_interval,
    volume_updated_at: Math.max(...histories.map((data) => finiteRevisionValue(data.volume_updated_at))) || null,
  }
}

/** Price and CVD snapshots have independent revisions from OI observations. */
export function mergeTradeCharts(
  existing: Partial<ScreenerExchangeData>, incoming: Partial<ScreenerExchangeData>,
): Partial<ScreenerExchangeData> {
  const result: Partial<ScreenerExchangeData> = {}
  const copy = (keys: readonly (keyof ScreenerExchangeData)[], source: Partial<ScreenerExchangeData>) => {
    for (const key of keys) Object.assign(result, { [key]: source[key] })
  }
  const price = incoming.candle_updated_at != null
    && finiteRevisionValue(incoming.candle_updated_at) >= finiteRevisionValue(existing.candle_updated_at)
    ? incoming : existing
  if (price.candle_updated_at != null) copy([
    'candles', 'trade_price_history', 'candle_interval', 'candle_source', 'candle_error',
    'candle_native_instrument', 'candle_quote_currency', 'candle_updated_at', 'market',
  ], price)
  if (price.market != null && price.candle_updated_at != null) {
    // A new native futures source can replace a spot fallback. Keep ticker
    // metadata only from the same market, even if an older full request wins.
    const matching = [existing, incoming].filter(data => data.market === price.market)
      .sort((a, b) => finiteRevisionValue(b.updated_at) - finiteRevisionValue(a.updated_at))
    const ticker = matching.find(data => data.last_price !== undefined || data.volume_24h !== undefined)
    result.last_price = ticker?.last_price ?? null
    result.volume_24h = ticker?.volume_24h ?? null
    result.updated_at = ticker?.updated_at ?? null
  }
  const cvd = incoming.cvd_updated_at != null
    && finiteRevisionValue(incoming.cvd_updated_at) >= finiteRevisionValue(existing.cvd_updated_at)
    ? incoming : existing
  // A late execution revises every later cumulative value. Replace all points
  // and their baseline together, including explicit gaps and empty histories.
  if (cvd.cvd_updated_at != null) copy([
    'cvd_history', 'cvd_interval', 'cvd_unit', 'cvd_baseline_ts', 'cvd_updated_at',
    'cvd_has_gaps', 'cvd_error',
  ], cvd)
  return result
}

export function mergeOIHistory(
  existingHistory: ScreenerOIDataPoint[] = [],
  incomingHistory: ScreenerOIDataPoint[] = [],
  maxPoints = 500,
): ScreenerOIDataPoint[] {
  const historyByTimestamp = new Map<number, ScreenerOIDataPoint>()
  for (const point of [...existingHistory, ...incomingHistory]) {
    const existing = historyByTimestamp.get(point.timestamp)
    if (
      !existing
      || compareOIRevision(
        {
          timestamp: point.timestamp,
          sampleKind: point.sampleKind,
          quality: point.quality,
          observedAt: point.observedAt,
          readRevision: point.readRevision,
        },
        {
          timestamp: existing.timestamp,
          sampleKind: existing.sampleKind,
          quality: existing.quality,
          observedAt: existing.observedAt,
          readRevision: existing.readRevision,
        },
      ) > 0
    ) {
      historyByTimestamp.set(point.timestamp, point)
    } else if (!existing.ohlc && point.ohlc
      && existing.value === point.value && existing.valueUsdt === point.valueUsdt
      && existing.price === point.price
      && compareOIRevision(existing, point) === 0) {
      // Optional OHLC enrichment may recover after a timeout without a new
      // observation. Add its missing candle without replacing OI provenance.
      historyByTimestamp.set(point.timestamp, { ...existing, ohlc: point.ohlc })
    }
  }
  return Array.from(historyByTimestamp.values())
    .sort((a, b) => a.timestamp - b.timestamp)
    .slice(-maxPoints)
}

export interface ScreenerMergeWindow {
  from?: number
  to?: number
  maxPoints?: number
  /** CVD is a complete cumulative snapshot, never a timestamp append. */
  replaceCvd?: boolean
  /** Used by explicitly scoped optional snapshots when the primary metric had no venue data. */
  allowNewExchanges?: boolean
}

/** Merge a bounded live tail without discarding the loaded historical prefix. */
export function mergeScreenerTail(
  existing: ScreenerTickerData,
  incoming: ScreenerTickerData,
  options: ScreenerMergeWindow = {},
): ScreenerTickerData {
  if (existing.symbol.toUpperCase() !== incoming.symbol.toUpperCase()) return existing
  const maxPoints = options.maxPoints ?? 5000
  const clip = <T extends { timestamp: number }>(points: T[] = [], limit = maxPoints): T[] => points
    .filter(point => Number.isFinite(point.timestamp)
      && (options.from === undefined || point.timestamp >= options.from)
      && (options.to === undefined || point.timestamp <= options.to))
    .slice(-limit)
  const union = <T extends { timestamp: number }>(old: T[] = [], next: T[] = []): T[] => {
    const byTime = new Map(old.map(point => [point.timestamp, point]))
    for (const point of next) byTime.set(point.timestamp, point)
    return clip([...byTime.values()].sort((a, b) => a.timestamp - b.timestamp))
  }
  const exchanges = { ...existing.exchanges }
  const authority = incoming.oi_authoritative_window
  const sameOI = !incoming.oi_interval || !existing.oi_interval || incoming.oi_interval === existing.oi_interval
  const authoritativeOI = !!authority && sameOI && Number.isFinite(authority.read_started_at)
    && authority.read_started_at >= finiteRevisionValue(existing.oi_read_started_at ?? existing.read_started_at)
  const inAuthoritativeWindow = (timestamp: number) => !!authority
    && timestamp >= authority.from_ts && timestamp <= authority.to_ts
  // An authoritative read may delete the last point for a venue, in which case
  // the delta endpoint has no exchange row. Absence inside this window is data.
  const venues = authoritativeOI ? new Set([...Object.keys(existing.exchanges), ...Object.keys(incoming.exchanges)])
    : new Set(Object.keys(incoming.exchanges))
  for (const venue of venues) {
    const next: Partial<ScreenerExchangeData> = incoming.exchanges[venue] || {}
    const old = exchanges[venue] ?? (options.allowNewExchanges ? {
      candles: [], oi_current: null, oi_current_usdt: null, last_price: null, volume_24h: null, updated_at: null,
    } : undefined)
    if (!old) continue // The scope is owned by the initial snapshot.
    const samePrice = !next.candle_interval || !old.candle_interval
      || next.candle_interval === old.candle_interval
    const sameVolume = !next.volume_interval || !old.volume_interval
      || next.volume_interval === old.volume_interval
    const comparison = compareOIRevision(exchangeOIRevision(next), exchangeOIRevision(old))
    const merged = { ...old, ...mergeFundingSnapshot(old, next) }
    if (next.orderbook !== undefined) merged.orderbook = next.orderbook
    if (authoritativeOI) {
      merged.oi_history = clip(mergeOIHistory((old.oi_history || []).filter(point => !inAuthoritativeWindow(point.timestamp)),
        next.oi_history || [], maxPoints))
      // Recompute the header when its latest observation was revised/deleted.
      // Raw source precedence still applies outside an authoritative window.
      const latest = merged.oi_history[merged.oi_history.length - 1]
      if (inAuthoritativeWindow(exchangeOIRevision(old).timestamp || 0)
        || (latest && inAuthoritativeWindow(latest.timestamp))) {
        const origin = latest?.valueOriginAt ?? latest?.observedAt ?? latest?.timestamp
        Object.assign(merged, {
          oi_current: latest?.value ?? null, oi_current_usdt: latest?.valueUsdt ?? null,
          oi_updated_at: latest?.timestamp ?? null, oi_observed_at: latest?.observedAt ?? null,
          oi_quality: latest?.quality, oi_sample_kind: latest?.sampleKind,
          oi_read_revision: latest?.readRevision,
          oi_source_age_seconds: origin == null ? null : Math.max(0, authority!.read_started_at - origin),
        })
      }
    } else if (!authority && sameOI && next.oi_history !== undefined) {
      if (comparison > 0 || !hasOIRevision(old)) {
        for (const key of ['oi_current', 'oi_current_usdt', 'oi_updated_at', 'oi_observed_at',
          'oi_quality', 'oi_sample_kind', 'oi_source_age_seconds', 'oi_read_revision'] as const) {
          if (next[key] !== undefined) Object.assign(merged, { [key]: next[key] })
        }
      } else if (comparison === 0) {
        merged.oi_source_age_seconds = monotonicOISourceAge(old.oi_source_age_seconds, next.oi_source_age_seconds)
      }
      merged.oi_history = clip(mergeOIHistory(old.oi_history, next.oi_history, maxPoints))
    }
    if (sameVolume && next.volume_history !== undefined) {
      Object.assign(merged, mergeVolumeHistory(old, next, merged.oi_history || [], maxPoints))
      merged.volume_history = clip(merged.volume_history, maxPoints + (merged.oi_history?.length || 0))
    }
    if (samePrice && next.candle_updated_at != null
      && finiteRevisionValue(next.candle_updated_at) >= finiteRevisionValue(old.candle_updated_at)) {
      const charts = mergeTradeCharts(old, { ...next, cvd_updated_at: undefined })
      Object.assign(merged, charts)
      const sameSource = (!old.market || !next.market || old.market === next.market)
        && (!old.candle_native_instrument || !next.candle_native_instrument
          || old.candle_native_instrument === next.candle_native_instrument)
      merged.candles = union(sameSource ? old.candles : [], next.candles)
      if (next.trade_price_history !== undefined) merged.trade_price_history = union(sameSource ? old.trade_price_history : [], next.trade_price_history)
    }
    const oldCvd = old.cvd_history || []
    const nextCvd = next.cvd_history || []
    const coversCvd = old.cvd_baseline_ts === next.cvd_baseline_ts
      && (!oldCvd.length || (!!nextCvd.length && nextCvd[0].timestamp <= oldCvd[0].timestamp
        && nextCvd[nextCvd.length - 1].timestamp >= oldCvd[oldCvd.length - 1].timestamp))
    if (next.cvd_history !== undefined && (options.replaceCvd ?? coversCvd)
      && (!old.cvd_interval || old.cvd_interval === next.cvd_interval)
      && finiteRevisionValue(next.cvd_updated_at) >= finiteRevisionValue(old.cvd_updated_at)) {
      for (const key of ['cvd_history', 'cvd_interval', 'cvd_unit', 'cvd_baseline_ts',
        'cvd_updated_at', 'cvd_has_gaps', 'cvd_error'] as const) {
        Object.assign(merged, { [key]: next[key] })
      }
    }
    exchanges[venue] = merged
  }
  return {
    ...existing,
    updated_at: Math.max(existing.updated_at || 0, incoming.updated_at || 0) || null,
    oi_history_until: Math.max(existing.oi_history_until || 0, incoming.oi_history_until || 0) || null,
    oi_read_started_at: authoritativeOI ? authority!.read_started_at : existing.oi_read_started_at,
    oi_authoritative_window: authoritativeOI ? authority : existing.oi_authoritative_window,
    oi_revision: (authority && !authoritativeOI) || incoming.oi_revision == null ? existing.oi_revision
      : Math.max(existing.oi_revision ?? 0, incoming.oi_revision),
    oi_revision_epoch: authority && !authoritativeOI ? existing.oi_revision_epoch : incoming.oi_revision_epoch ?? existing.oi_revision_epoch,
    oi_pending_from: (authority && !authoritativeOI) || incoming.oi_pending_from === undefined ? existing.oi_pending_from : incoming.oi_pending_from,
    oi_pending_signature: (authority && !authoritativeOI) || incoming.oi_pending_signature === undefined ? existing.oi_pending_signature : incoming.oi_pending_signature,
    oi_pending_overflow: authority && !authoritativeOI ? existing.oi_pending_overflow : incoming.oi_pending_overflow ?? existing.oi_pending_overflow,
    oi_raw_closed_before: authority && !authoritativeOI ? existing.oi_raw_closed_before : incoming.oi_raw_closed_before ?? existing.oi_raw_closed_before,
    live_oi: incoming.live_oi ?? existing.live_oi,
    orderbook_sources: incoming.orderbook_sources ?? existing.orderbook_sources,
    exchanges,
  }
}
